<template>
    <!-- "Tus reservas" de Mi cuenta (realizadas / recibidas). Mismo diseño que
         Editar perfil: lateral con título, cifras y la navegación de la cuenta;
         a la derecha las pestañas (solo anfitriones) y las tarjetas. La
         información de cada tarjeta es la de las listas anteriores. -->
    <div class="bk bk--full">
        <aside class="bk-side">
            <h1 class="bk-title">{{ $t('nuevo_menu.rv.title_a') }} <span class="bk-grad">{{ $t('nuevo_menu.rv.title_b') }}</span></h1>
            <p class="bk-subtitle">{{ $t(recibidas ? 'nuevo_menu.rv.sub_received' : 'nuevo_menu.rv.sub_made') }}</p>
            <div class="bk-stats">
                <div class="bk-stat"><p class="bk-stat-n">{{ resumen.total }}</p><p class="bk-stat-l">{{ $t('nuevo_menu.rv.stat_total') }}</p></div>
                <div class="bk-stat bk-stat--wait"><p class="bk-stat-n">{{ resumen.pendientes }}</p><p class="bk-stat-l">{{ $t(recibidas ? 'nuevo_menu.rv.stat_to_answer' : 'nuevo_menu.rv.stat_pending') }}</p></div>
                <div class="bk-stat bk-stat--ok"><p class="bk-stat-n">{{ resumen.pagadas }}</p><p class="bk-stat-l">{{ $t('nuevo_menu.rv.stat_paid') }}</p></div>
            </div>
            <CuentaNav />
        </aside>

        <!-- Mientras llega otra página, la actual queda atenuada (aria-busy). -->
        <div :class="['bk-list-main', { 'is-cargando': cargando && lista.length }]" :aria-busy="String(cargando)">
            <nav v-if="isHost" class="bk-tabs" :aria-label="$t('nuevo_menu.rv.tabs')">
                <router-link to="/reservations-made" :class="['bk-tab', { 'is-active': !recibidas }]" :aria-current="!recibidas ? 'page' : null">{{ $t('nuevo_menu.nav.made') }}</router-link>
                <router-link to="/reservations-received" :class="['bk-tab', { 'is-active': recibidas }]" :aria-current="recibidas ? 'page' : null">{{ $t('nuevo_menu.nav.received') }}</router-link>
            </nav>

            <div v-if="cargando && !lista.length" class="bk-loading-box"><HyFLoader variant="inline" /></div>

            <div v-else-if="error" class="bk-empty" role="alert">
                <div class="bk-empty-ico" aria-hidden="true" v-html="ICON.cal"></div>
                <p class="bk-empty-title">{{ $t('nuevo_menu.rv.error') }}</p>
                <p class="bk-empty-text">{{ $t('nuevo_menu.rv.error_text') }}</p>
                <button type="button" class="bk-btn bk-btn--primary" @click="recargar">{{ $t('nuevo_menu.retry') }}</button>
            </div>

            <div v-else-if="!lista.length" class="bk-empty">
                <div class="bk-empty-ico" aria-hidden="true" v-html="ICON.cal"></div>
                <p class="bk-empty-title">{{ $t('nuevo_menu.rv.empty') }}</p>
                <p class="bk-empty-text">{{ $t(recibidas ? 'nuevo_menu.rv.empty_received' : 'nuevo_menu.rv.empty_made') }}</p>
            </div>

            <article
                v-for="r in tarjetas"
                v-else
                :key="r.id"
                :class="['bk-rv', `bk-rv--${r.e.c}`]"
                :aria-labelledby="`rv-${r.id}`"
            >
                <header class="bk-rv-top">
                    <span :class="['bk-badge', `bk-badge--${r.e.badge}`]">{{ $t(`nuevo_menu.rv.st.${r.e.t}`) }}</span>
                    <ol class="bk-rv-steps" :aria-label="$t('nuevo_menu.rv.steps')">
                        <li v-for="p in r.pasos" :key="p.k" :class="['bk-rv-step', p.st ? `is-${p.st}` : '']">
                            <span class="bk-rv-dot" aria-hidden="true">{{ p.st === 'done' ? '✓' : p.st === 'end' ? '✕' : '' }}</span>
                            <span>{{ $t(`nuevo_menu.rv.step.${p.k}`) }}</span>
                            <span v-if="p.st === 'now'" class="bk-sr-only">({{ $t('nuevo_menu.rv.step_now') }})</span>
                        </li>
                    </ol>
                    <span class="bk-rv-id">{{ $t('nuevo_menu.rv.number', { n: r.id }) }}</span>
                </header>

                <div class="bk-rv-grid">
                    <div class="bk-rv-col">
                        <router-link v-if="r.alojamientoId" class="bk-rv-place" :to="`/alojamiento/${r.alojamientoId}`">
                            <span class="bk-rv-place-ico" aria-hidden="true" v-html="ICON.home"></span>
                            <span class="bk-rv-place-txt">
                                <span class="bk-rv-k">{{ $t('nuevo_menu.rv.place') }}</span>
                                <span :id="`rv-${r.id}`" class="bk-rv-place-name">{{ r.alojamiento }}</span>
                                <span v-if="r.verDireccion" class="bk-rv-addr"><span v-html="ICON.pin"></span>{{ r.direccion }}</span>
                                <span v-else class="bk-rv-addr is-locked"><span v-html="ICON.lock"></span>{{ $t('nuevo_menu.rv.addr_locked') }}</span>
                            </span>
                            <span class="bk-rv-go" aria-hidden="true" v-html="ICON.next"></span>
                            <span class="bk-sr-only">{{ $t('nuevo_menu.rv.see_place') }}</span>
                        </router-link>

                        <div class="bk-rv-dates">
                            <div class="bk-rv-date">
                                <span class="bk-rv-k">{{ $t('nuevo_menu.rv.check_in') }}</span>
                                <span class="bk-rv-day">{{ r.ent.dia }}</span><span class="bk-rv-month">{{ r.ent.mes }}</span><span class="bk-rv-week">{{ r.ent.semana }}</span>
                            </div>
                            <div class="bk-rv-nights" aria-hidden="true">
                                <span class="bk-rv-line"></span><span class="bk-rv-nights-n">{{ $tc('nuevo_menu.rv.nights', r.noches, { n: r.noches }) }}</span><span class="bk-rv-line"></span>
                            </div>
                            <div class="bk-rv-date bk-rv-date--end">
                                <span class="bk-rv-k">{{ $t('nuevo_menu.rv.check_out') }}</span>
                                <span class="bk-rv-day">{{ r.sal.dia }}</span><span class="bk-rv-month">{{ r.sal.mes }}</span><span class="bk-rv-week">{{ r.sal.semana }}</span>
                            </div>
                            <span class="bk-sr-only">{{ $tc('nuevo_menu.rv.nights', r.noches, { n: r.noches }) }}</span>
                        </div>
                    </div>

                    <div class="bk-rv-col bk-rv-person">
                        <div v-if="r.verNombre" class="bk-rv-who">
                            <span class="bk-rv-av" aria-hidden="true">{{ r.persona.ini }}</span>
                            <span>
                                <span class="bk-rv-k">{{ $t(recibidas ? 'nuevo_menu.rv.guest' : 'nuevo_menu.rv.host') }}</span>
                                <span class="bk-rv-name">{{ r.persona.nombre }}</span>
                            </span>
                        </div>
                        <ul v-if="r.verContacto && (r.persona.correo || r.persona.telefono)" class="bk-rv-contact">
                            <li v-if="r.persona.correo"><a :href="`mailto:${r.persona.correo}`"><span v-html="ICON.mail"></span><span>{{ r.persona.correo }}</span></a></li>
                            <li v-if="r.persona.telefono"><a :href="`tel:${r.persona.telTel}`"><span v-html="ICON.tel"></span><span>{{ r.persona.telefono }}</span></a></li>
                        </ul>
                        <p v-else-if="!r.verContacto" class="bk-rv-locked"><span v-html="ICON.lock"></span><span>{{ $t('nuevo_menu.rv.contact_locked') }}</span></p>
                    </div>
                </div>

                <footer class="bk-rv-foot">
                    <p class="bk-rv-note">{{ $t(`nuevo_menu.rv.note.${r.e.nota}`) }}</p>
                    <a v-if="r.pagar" :href="r.pagar" class="bk-btn bk-btn--primary">{{ $t('nuevo_menu.rv.pay') }} <span v-html="ICON.next"></span></a>
                </footer>
            </article>

            <BkPaginacion v-if="!redirigiendo && !error" :actual="datos.current_page || 1" :ultima="datos.last_page || 1" :total="datos.total" @ir="irAPagina" />
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import BkPaginacion from './BkPaginacion.vue';
import HyFLoader from '@/components/Loader/HyFLoader.vue';
import CuentaNav from './CuentaNav.vue';
import ICON from './iconos';

// Anfitriones con reserva auto-aceptada y pago por fuera de la plataforma
// (Homiii): no se muestra "Ir a pagar" (mismo criterio que antes).
const HOSTS_PAGO_FUERA = ['6694'];
const LOCALES = { es: 'es-CL', en: 'en-US', pt: 'pt-BR', fr: 'fr-FR', it: 'it-IT', de: 'de-DE' };
// Reservas por página (el mismo valor que usaba el paginador anterior).
const POR_PAGINA = 10;

export default {
    name: 'ReservasCuenta',
    components: { BkPaginacion, HyFLoader, CuentaNav },
    props: {
        // realizadas | recibidas
        tipo: { type: String, required: true },
    },
    data() {
        return { ICON, cargando: true, error: false, ultimo: null, redirigiendo: false, shareUrl: (process.env.VUE_APP_SHARE_URL || '').replace(/\/+$/, '') };
    },
    computed: {
        ...mapState('reservationNew', ['madeReservations', 'receivedReservations']),
        ...mapState(['esAnfitrion']),
        ...mapGetters(['isHost']),
        recibidas() { return this.tipo === 'recibidas'; },
        datos() { return (this.recibidas ? this.receivedReservations : this.madeReservations) || { data: [] }; },
        lista() { return Array.isArray(this.datos.data) ? this.datos.data : []; },
        resumen() {
            const r = this.datos.resumen || {};
            return { total: r.total != null ? r.total : (this.datos.total || 0), pendientes: r.pendientes || 0, pagadas: r.pagadas || 0 };
        },
        tarjetas() { return this.lista.map(this.armar); },
    },
    watch: {
        // Un huésped sin alojamientos no tiene reservas recibidas: va a las realizadas.
        esAnfitrion: { immediate: true, handler() { this.revisarAcceso(); } },
    },
    methods: {
        revisarAcceso() {
            if (this.recibidas && !this.isHost && this.esAnfitrion === '0') {
                // Sin la lista (y sin el paginador, que también cambia la URL al crearse).
                this.redirigiendo = true;
                this.$nextTick(() => this.$router.replace('/reservations-made').catch(() => {}));
            }
        },
        async cargar(params) {
            this.ultimo = params;
            this.cargando = true;
            this.error = false;
            try {
                await this.$store.dispatch(this.recibidas ? 'reservationNew/fetchReceivedReservations' : 'reservationNew/fetchMadeReservations', params);
            } catch (e) {
                console.error('[ReservasCuenta] no se pudieron leer las reservas', e);
                this.error = true;
            } finally {
                this.cargando = false;
            }
        },
        recargar() {
            this.cargar(this.ultimo || { page: 1, perPage: POR_PAGINA });
        },
        // Página nueva: se pide, se anota en la URL (?page=) y se vuelve arriba.
        irAPagina(page) {
            this.$router.replace({ query: { ...this.$route.query, page: String(page) } }).catch(() => {});
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.cargar({ page, perPage: POR_PAGINA });
        },

        nombreAlojamiento(texto) {
            try {
                const j = JSON.parse(texto);
                return j[this.$i18n.locale] || j.es || j.en || Object.values(j)[0] || '';
            } catch (e) {
                return texto || '';
            }
        },
        dia(valor) {
            const iso = String(valor || '').slice(0, 10);
            const d = new Date(iso + 'T12:00:00');
            if (isNaN(d)) return { dia: '–', mes: '', semana: '' };
            const loc = LOCALES[this.$i18n.locale] || 'es';
            const f = o => new Intl.DateTimeFormat(loc, o).format(d);
            return { dia: f({ day: 'numeric' }), mes: f({ month: 'short', year: 'numeric' }).replace('.', ''), semana: f({ weekday: 'long' }) };
        },
        // c: color · badge · t: texto del estado · nota · paso (1 solicitada, 2 aceptada, 3 pagada)
        estado(r, homi) {
            const lado = this.recibidas ? 'host' : 'guest';
            if (r.fecha_pago) return { c: 'ok', badge: 'ok', paso: 3, t: 'paid', nota: `paid_${lado}` };
            if (r.status === 'accepted') return { c: 'pay', badge: 'pay', paso: 2, t: `accepted_${lado}`, nota: this.recibidas ? 'accepted_host' : (homi ? 'accepted_homi' : 'accepted_guest') };
            if (r.status === 'rejected') return { c: 'bad', badge: 'bad', paso: 1, fin: 'rejected', t: 'rejected', nota: 'rejected' };
            if (r.status === 'canceled') return { c: 'off', badge: 'plain', paso: 1, fin: 'canceled', t: 'canceled', nota: 'canceled' };
            return { c: 'wait', badge: 'wait', paso: 1, t: `initiated_${lado}`, nota: `initiated_${lado}` };
        },
        armar(r) {
            const acc = r.accommodation || {};
            const dueno = acc.usuario_idusuario || (acc.user && acc.user.idusuario);
            const homi = HOSTS_PAGO_FUERA.includes(String(dueno));
            const e = this.estado(r, homi);
            const p = (this.recibidas ? r.user : acc.user) || {};
            const nombre = [p.nombre, p.apellido].filter(v => v && v !== 'null').join(' ') || '—';
            const ini = nombre.split(/\s+/).map(x => x.charAt(0)).join('').slice(0, 2).toUpperCase();
            const desde = String(r.fecha_inicio || '').slice(0, 10), hasta = String(r.fecha_termino || '').slice(0, 10);
            const noches = Math.max(0, Math.round((new Date(hasta + 'T12:00:00') - new Date(desde + 'T12:00:00')) / 864e5)) || 0;
            const pasos = e.fin
                ? [{ k: 'requested', st: 'done' }, { k: e.fin, st: 'end' }]
                : [{ k: 'requested', st: 'done' }, { k: 'accepted', st: e.paso >= 2 ? 'done' : 'now' }, { k: 'paid', st: e.paso >= 3 ? 'done' : (e.paso === 2 ? 'now' : '') }];
            const pagada = !!r.fecha_pago;
            return {
                id: r.idreserva,
                e,
                pasos,
                alojamientoId: acc.idalojamiento || acc.id,
                alojamiento: this.nombreAlojamiento(acc.nombre),
                direccion: acc.direccion || '',
                // Como antes: el huésped ve la dirección y el contacto del anfitrión
                // recién con el pago; el anfitrión ve siempre la dirección de su
                // espacio y el contacto del huésped también con el pago.
                verDireccion: this.recibidas || pagada,
                verContacto: pagada,
                // El huésped tampoco ve el nombre del anfitrión hasta pagar.
                verNombre: this.recibidas || pagada,
                ent: this.dia(desde),
                sal: this.dia(hasta),
                noches,
                persona: { nombre, ini, correo: p.correo || '', telefono: p.telefono || '', telTel: String(p.telefono || '').replace(/[^\d+]/g, '') },
                pagar: !this.recibidas && r.status === 'accepted' && !pagada && !homi ? `${this.shareUrl}/pagos/${r.idreserva}/${r.apitoken}` : null,
            };
        },
    },
    created() {
        window.scrollTo(0, 0);
        if (!this.redirigiendo) {
            const page = Number(this.$route.query.page) > 0 ? Number(this.$route.query.page) : 1;
            this.cargar({ page, perPage: POR_PAGINA });
        }
    },
};
</script>

<style>
/* Las <span> que solo llevan un ícono no agregan caja propia. */
.bk-rv-addr > span:first-child, .bk-rv-locked > span:first-child, .bk-rv-contact a > span:first-child, .bk-rv-foot .bk-btn > span { display: contents; }
.bk-rv-foot .bk-btn > span svg { width: 18px; height: 18px; }
</style>
